import '@app/components/BranchSelect/styles/BranchSelect.desktop.scss';

import {
  CheckOutlined,
  LockOutlined,
  PlusOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Collapse, Modal, Skeleton, Space, Tooltip, Typography } from 'antd';
import cn from 'classnames';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useGetUserBranchesQuery } from '@app/api/Branches';
import { BillingStatuses } from '@app/api/Branches/types';
import { useSetUserActiveBranchMutation } from '@app/api/User/api';
import type { NBranchSelect } from '@app/components/BranchSelect/BranchSelect.types';
import {
  BRANCHES_PATH,
  CALENDAR_PATH,
  CHAINS_PATH,
  PROFILE_PATH,
  WARNING,
} from '@app/constants';
import { PREFIX } from '@app/constants/appPrefix';
import {
  useNewUserAccessRights,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import {
  selectUser,
  setActiveBranchCountryId,
  setActiveBranchName,
  setUser,
  useAppDispatch,
  useAppSelector,
} from '@app/store';
import type { NAuth } from '@app/types/Auth';
import { generateKey, setActiveBranchSession } from '@app/utils';

const { Text } = Typography;

export const BranchSelect: NBranchSelect.Main = ({ open, setBranchSelect }) => {
  const {
    userAccessRights: { Branches, Chains, Records },
  } = useNewUserAccessRights();
  const rightToCreateBranch = Branches?.IsCreate;
  const rightToCreateChain = Chains?.IsCreate;
  const hasUserRightToJournal = Records?.IsJournal;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { activeBranchId: localActiveBranchId } = useUserPrint();
  const [isLoader, setLoader] = useState(false);
  const user = useAppSelector(selectUser);
  const { innerWidth } = useWindowSize();
  const { showNotification } = useContext(NotificationContext);
  const [setUserActiveBranch] = useSetUserActiveBranchMutation();

  const {
    data: branches,
    error,
    isLoading: isGetBranchesLoading,
  } = useGetUserBranchesQuery(
    { ActiveBranchId: localActiveBranchId },
    { skip: !open || !localActiveBranchId, refetchOnMountOrArgChange: true }
  );

  const isLoading = isGetBranchesLoading || isLoader;

  useEffect(() => {
    if (error) {
      showNotification(handleCatchError(error));
    }
  }, [error, showNotification]);

  const changeBranch = useCallback(
    async (newActiveBranchId: NAuth.UserPrint['activeBranchId']) => {
      if (newActiveBranchId == null) return;

      const newBranch = (branches || []).find(
        (item) => item.BranchId === newActiveBranchId
      );
      const newBranchName = newBranch?.Name || 'Филиал без названия';
      const newBranchCountryId = newBranch?.CountryId ?? null;

      setLoader(true);
      setBranchSelect(false);
      setActiveBranchSession({
        activeBranchId: newActiveBranchId,
        activeBranchName: newBranchName,
        activeBranchCountryId: newBranchCountryId,
        activeBranchUserId: user?.userId ?? null,
      });
      dispatch(
        setUser({
          ...user,
          activeBranchId: newActiveBranchId,
        })
      );
      dispatch(setActiveBranchName(newBranchName));

      if (newBranchCountryId != null) {
        dispatch(setActiveBranchCountryId(newBranchCountryId));
      }

      navigate(hasUserRightToJournal ? CALENDAR_PATH : PROFILE_PATH);

      try {
        await setUserActiveBranch({
          ActiveBranchId: localActiveBranchId,
          branchId: newActiveBranchId,
        }).unwrap();
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      } finally {
        setLoader(false);
      }
    },
    [
      branches,
      dispatch,
      hasUserRightToJournal,
      localActiveBranchId,
      navigate,
      showNotification,
      setBranchSelect,
      user,
      setUserActiveBranch,
    ]
  );

  const renderBillingStatus = (billingStatus: BillingStatuses | null) => {
    switch (billingStatus) {
      case BillingStatuses.PreBlock:
        return (
          <Tooltip title="Предупреждение о блокировке за задолженность">
            <WarningOutlined className="warning-icon" />
          </Tooltip>
        );

      case BillingStatuses.Block:
        return (
          <Tooltip title="Заблокирован за задолженность">
            <LockOutlined className="lock-icon" />
          </Tooltip>
        );

      default:
        return null;
    }
  };

  const memoizedData = useMemo(() => {
    const renderedChainNames: Array<string | null> = [];
    return branches?.map((item) => {
      const { Chain, ChainId } = item;
      const activeBranchChainId = branches?.find(
        (_item) => _item.BranchId === localActiveBranchId
      )?.ChainId;
      const isChainRendered = () =>
        renderedChainNames?.filter((_item) => _item === Chain)?.length > 0;

      if (!isChainRendered()) {
        renderedChainNames.push(Chain);

        return (
          <Collapse
            key={generateKey('BranchSelect', 'Collapse', String(ChainId))}
            bordered={false}
            defaultActiveKey={[String(activeBranchChainId)]}
            items={[
              {
                key: String(ChainId),
                className: 'chain-title',
                label: `${Chain || 'Филиалы без сети'}`,
                children: (
                  <Space direction="vertical" size="middle">
                    {branches.map((_item) => {
                      const {
                        Chain: _Chain,
                        BranchId: _BranchId,
                        Name: _Name,
                        BillingStatus,
                      } = _item;
                      const isActiveBranch = _BranchId === localActiveBranchId;

                      if (Chain === _Chain) {
                        return (
                          <div
                            key={generateKey(
                              'BranchSelect',
                              'branches',
                              `${_BranchId}-${_Chain}`
                            )}
                          >
                            <Text
                              className={cn('branch-title', {
                                'inactive-branch': !isActiveBranch,
                              })}
                              onClick={
                                isActiveBranch
                                  ? undefined
                                  : () => changeBranch(_BranchId)
                              }
                            >
                              <Space>
                                {_Name}
                                {renderBillingStatus(BillingStatus)}
                                <span>
                                  {isActiveBranch && (
                                    <CheckOutlined
                                      style={{ color: '#1677ff' }}
                                    />
                                  )}
                                </span>
                              </Space>
                            </Text>
                          </div>
                        );
                      }
                    })}
                  </Space>
                ),
              },
            ]}
          ></Collapse>
        );
      }
    });
  }, [branches, changeBranch, localActiveBranchId]);

  const isOptimalDialogWidth = useMemo(() => innerWidth > 480, [innerWidth]);

  const buttonStyles = useMemo(() => {
    return {
      width: isOptimalDialogWidth ? '49%' : '100%',
    };
  }, [isOptimalDialogWidth]);

  return (
    <Modal
      className={`${PREFIX}branch-select`}
      title="Выбор филиала"
      open={open}
      okText="Добавить сеть"
      okButtonProps={{
        icon: <PlusOutlined />,
        style: {
          ...buttonStyles,
          ...(rightToCreateChain ? {} : { display: 'none' }),
          marginTop: isOptimalDialogWidth ? '0px' : '8px',
          marginLeft: isOptimalDialogWidth ? '8px' : '0px',
        },
        onClick: () => {
          setBranchSelect(false);
          navigate(`${CHAINS_PATH}/add`);
        },
      }}
      cancelText="Добавить филиал"
      cancelButtonProps={{
        type: 'primary',
        icon: <PlusOutlined />,
        style: {
          ...buttonStyles,
          ...(rightToCreateBranch ? {} : { display: 'none' }),
        },
        onClick: () => {
          setBranchSelect(false);
          navigate(`${BRANCHES_PATH}/add`);
        },
      }}
      onCancel={() => setBranchSelect(false)}
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        {isLoading ? <Skeleton active /> : memoizedData}
      </Space>
    </Modal>
  );
};
